import { useState, useEffect, useCallback } from 'react';
import { getStorageData, setStorageData, removeStorageData } from '@/lib/storage';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

export type WorkspaceMode = 'job_search' | 'tailor';
export type AppMode = 'initial' | WorkspaceMode;

export function useWorkspacesState() {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.uid;

  const [workspaces, setWorkspaces] = useState<{id: string, name: string, mode: WorkspaceMode}[]>([]);
  const [appMode, setAppMode] = useState<'initial' | WorkspaceMode>('initial');
  const [activeIds, setActiveIds] = useState<Record<WorkspaceMode, string | null>>({ job_search: null, tailor: null });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    let active = true;
    const loadCoreState = async () => {
      try {
        let savedV3 = await getStorageData<{id: string, name: string, mode: WorkspaceMode}[]>('resumeChecker_workspaces_v3', userId);
        
        // Migrate from uncompressed localStorage if needed
        if (!savedV3 || savedV3.length === 0) {
          const lsData = localStorage.getItem('resumeChecker_workspaces_v3');
          if (lsData && lsData !== 'undefined') {
            const parsed = JSON.parse(lsData);
            if (parsed && parsed.length > 0) {
              savedV3 = parsed;
              await setStorageData('resumeChecker_workspaces_v3', savedV3, userId);
            }
          }
        }

        if (savedV3 && savedV3.length > 0) {
          if (active) setWorkspaces(savedV3);
        } else {
          let savedV2 = await getStorageData<any[]>('resumeChecker_workspaces', userId);
          if (savedV2 && savedV2.length > 0) {
            if (active) setWorkspaces(savedV2.map((w: any) => ({ ...w, mode: 'tailor' })));
          }
        }

        let savedActive = await getStorageData<Record<WorkspaceMode, string | null>>('resumeChecker_activeIds_v3', userId);
        
        if (savedActive && (savedActive.job_search || savedActive.tailor)) {
          if (active) setActiveIds(savedActive);
        } else if (savedV3 && savedV3.length > 0) {
          if (active) setActiveIds({ job_search: null, tailor: savedV3[0].id });
        }
      } catch (e) {} finally {
        if (active) setIsLoaded(true);
      }
    };
    
    setIsLoaded(false);
    loadCoreState();
    return () => { active = false; };
  }, [userId, authLoading]);

  // Sync state explicitly instead of via useEffect to prevent derived state infinite loops/renders
  const persistWorkspaces = useCallback(async (newWorkspaces: typeof workspaces) => {
    setWorkspaces(newWorkspaces);
    if (isLoaded) {
      await setStorageData('resumeChecker_workspaces_v3', newWorkspaces, userId);
    }
  }, [isLoaded, userId]);

  const persistActiveIds = useCallback(async (newActiveIds: typeof activeIds) => {
    setActiveIds(newActiveIds);
    if (isLoaded) {
      await setStorageData('resumeChecker_activeIds_v3', newActiveIds, userId);
    }
  }, [isLoaded, userId]);

  const handleSelectMode = useCallback((mode: WorkspaceMode) => {
    let currentActive = activeIds[mode];
    const modeWorkspaces = workspaces.filter(w => w.mode === mode);
    
    if (!currentActive || !modeWorkspaces.find(w => w.id === currentActive)) {
      if (modeWorkspaces.length > 0) {
        currentActive = modeWorkspaces[0].id;
      } else {
        const id = Date.now().toString();
        currentActive = id;
        persistWorkspaces([...workspaces, { id, name: `Workspace 1`, mode }]);
      }
      persistActiveIds({ ...activeIds, [mode]: currentActive });
    }
    
    setAppMode(mode);
  }, [activeIds, workspaces, persistActiveIds, persistWorkspaces]);

  const handleAddWorkspace = useCallback(() => {
    if (appMode === 'initial') return;
    const modeWorkspaces = workspaces.filter(w => w.mode === appMode);
    
    if (modeWorkspaces.length >= 10) {
      toast.error(`Maximum of 10 ${appMode === 'job_search' ? 'Job Finding' : 'Resume Analyze'} workspaces reached.`);
      return;
    }
    const id = Date.now().toString();
    persistWorkspaces([...workspaces, { id, name: `Workspace ${modeWorkspaces.length + 1}`, mode: appMode }]);
    persistActiveIds({ ...activeIds, [appMode]: id });
  }, [appMode, workspaces, activeIds, persistWorkspaces, persistActiveIds]);

  const handleCloseWorkspace = useCallback(async (id: string) => {
    const modeToClose = workspaces.find(w => w.id === id)?.mode;
    if (!modeToClose) return;

    const modeWorkspaces = workspaces.filter(w => w.mode === modeToClose);
    if (modeWorkspaces.length === 1) {
       toast.error("You must have at least one workspace open in this mode.");
       return;
    }
    
    const nextWorkspaces = workspaces.filter(ws => ws.id !== id);
    persistWorkspaces(nextWorkspaces);
    
    if (activeIds[modeToClose] === id) {
       const remaining = nextWorkspaces.filter(ws => ws.mode === modeToClose);
       persistActiveIds({ ...activeIds, [modeToClose]: remaining.length > 0 ? remaining[0].id : null });
    }
    
    try {
      await removeStorageData(`workspace_${id}`, userId);
    } catch (err) {}
  }, [workspaces, activeIds, persistWorkspaces, persistActiveIds, userId]);

  const handleRenameWorkspace = useCallback((id: string, newName: string) => {
    persistWorkspaces(workspaces.map(w => w.id === id ? { ...w, name: newName } : w));
  }, [workspaces, persistWorkspaces]);

  const handleGoHome = useCallback(() => {
    setAppMode('initial');
  }, []);

  const resetWorkspaceContent = useCallback(async () => {
    if (appMode === 'initial') return;
    const currentActiveId = activeIds[appMode];
    if (!currentActiveId) return;

    try {
      await removeStorageData(`workspace_${currentActiveId}`, userId);
      const newId = Date.now().toString();
      persistWorkspaces(workspaces.map(w => w.id === currentActiveId ? { ...w, id: newId } : w));
      persistActiveIds({ ...activeIds, [appMode]: newId });
    } catch (e) {}
  }, [appMode, activeIds, workspaces, persistWorkspaces, persistActiveIds, userId]);

  const importJob = useCallback(async (newData: any) => {
    try {
      const newId = Date.now().toString();
      let newName = newData.targetDetails?.company ? `${newData.targetDetails.company} - ${newData.targetDetails.position || 'Job'}` : `Job Search`;
      newName = newName.substring(0, 30);

      await setStorageData(`workspace_${newId}`, newData, userId);

      const tailorWorkspaces = workspaces.filter(w => w.mode === 'tailor');
      if (tailorWorkspaces.length >= 10) {
          toast.error('Maximum of 10 Resume Analyze workspaces reached.');
          return;
      }
      
      persistWorkspaces([...workspaces, { id: newId, name: newName, mode: 'tailor' }]);
      persistActiveIds({ ...activeIds, tailor: newId });
      setAppMode('tailor');
      toast.success('Job imported successfully!');
    } catch (err) {
      toast.error('Failed to import job');
    }
  }, [workspaces, activeIds, persistWorkspaces, persistActiveIds, userId]);

  const importJobToExisting = useCallback(async (newData: any, targetId: string) => {
    try {
      const existingData = await getStorageData<any>(`workspace_${targetId}`, userId) || {};
      const mergedData = {
          ...existingData, 
          jobText: newData.jobText, 
          jobLink: newData.jobLink, 
          targetDetails: {
              ...(existingData.targetDetails || {}),
              ...newData.targetDetails
          }
      };
      await setStorageData(`workspace_${targetId}`, mergedData, userId);

      persistActiveIds({ ...activeIds, tailor: targetId });
      setAppMode('tailor');
      toast.success('Job imported to workspace!');
    } catch (err) {
      toast.error('Failed to import job to existing workspace');
    }
  }, [activeIds, persistActiveIds, userId]);

  useEffect(() => {
    // URL params processing has been moved to AppPage.tsx to trigger the Modal
  }, [isLoaded]);

  return {
    workspaces,
    appMode,
    activeIds,
    isLoaded,
    handleSelectMode,
    handleAddWorkspace,
    handleCloseWorkspace,
    handleRenameWorkspace,
    handleGoHome,
    resetWorkspaceContent,
    importJob,
    importJobToExisting,
    persistActiveIds
  };
}
